import { ReactNode } from 'react';
import { Divider, Stack, styled, Typography } from '@mui/material';
import { error, neutral, success, warning } from 'theme/colors';

import { TrendingDown } from 'assets/icons/TrendingDown';
import { TrendingUp } from 'assets/icons/TrendingUp';

interface IResponseTimeProps {
  avgResponseTime: number;
  fastestResponseTime: number;
  slowestResponseTime: number;
}

interface IResponseProps {
  value: number;
  color?: string;
  title: string;
  description: string | ReactNode;
}

const Span = styled('span')`
  color: ${neutral[900]};
  font-size: 32px;
  font-weight: 600;
  line-height: 120%;
  margin: 16px 0 8px;
`;

const Response = ({ value, color, title, description }: IResponseProps) => {
  return (
    <Stack
      sx={{
        width: '30%',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color,
          textTransform: 'uppercase',
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Span>{value}s</Span>
      <Stack
        gap={0.5}
        direction="row"
        alignItems="center"
        sx={{
          opacity: 0.5,
        }}
      >
        {description}
      </Stack>
    </Stack>
  );
};

export const ResponseTime = ({
  avgResponseTime,
  fastestResponseTime,
  slowestResponseTime,
}: IResponseTimeProps) => {
  return (
    <div>
      <Typography variant="h6">Response Time</Typography>
      <Stack
        overflow="auto"
        maxWidth="100%"
        sx={{
          border: '1px solid',
          borderColor: 'secondary.light',
          borderRadius: '20px',
          marginTop: '16px',
        }}
      >
        <Stack
          pt={4}
          pb={2}
          px={3}
          gap={2}
          direction="row"
          justifyContent="space-between"
          divider={
            <Divider
              orientation="vertical"
              flexItem
            />
          }
        >
          <Response
            title="Average"
            value={avgResponseTime}
            description={<Typography variant="body2">Typical system response</Typography>}
            color={warning.main}
          />
          <Response
            title="Fastest"
            value={fastestResponseTime}
            description={
              <>
                <TrendingUp />
                <Typography
                  variant="body2"
                  color="success"
                >
                  Peak speed
                </Typography>
              </>
            }
            color={success.main}
          />
          <Response
            title="Slowest"
            value={slowestResponseTime}
            description={
              <>
                <TrendingDown />
                <Typography
                  variant="body2"
                  color="error"
                >
                  Max delay
                </Typography>
              </>
            }
            color={error.main}
          />
        </Stack>
      </Stack>
    </div>
  );
};
